import { useState, useEffect, useCallback } from 'react';
import { T, UI } from "../../constants/tokens";
import { adminListUsers, adminGetUserPositions, adminSetUserRole } from '../../lib/adminService';
import { dateNumeric } from '../../utils/formatDate';
import Icon from '../Icon';

const ROLE_META = {
  admin:    { bg: UI.line.hairline, text: UI.categorical[0], label: 'Admin'   },
  user:     { bg: UI.line.hairline, text: UI.categorical[2], label: 'פעיל'    },
  disabled: { bg: UI.line.hairline, text: UI.categorical[3], label: 'מושבת'   },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] ?? ROLE_META.user;
  return (
    <span style={{ background: m.bg, color: m.text, borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '.4px', flexShrink: 0 }}>
      {m.label}
    </span>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: '12px 14px', textAlign: 'center', flex: 1 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 10, color: UI.text.muted, fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function AdminDashboard({ currentUserId, onClose }) {
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [expanded,   setExpanded]   = useState(null);
  const [positions,  setPositions]  = useState({});
  const [posLoading, setPosLoading] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await adminListUsers());
    } catch (err) {
      setError(err.message ?? 'שגיאה בטעינת משתמשים');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const toggleExpand = async (userId) => {
    if (expanded === userId) { setExpanded(null); return; }
    setExpanded(userId);
    if (positions[userId] !== undefined) return;
    setPosLoading(userId);
    try {
      const data = await adminGetUserPositions(userId);
      setPositions(p => ({ ...p, [userId]: data }));
    } catch {
      setPositions(p => ({ ...p, [userId]: [] }));
    } finally {
      setPosLoading(null);
    }
  };

  const handleSetRole = async (e, userId, role) => {
    e.stopPropagation();
    setActionBusy(userId);
    try {
      await adminSetUserRole(userId, role);
      setUsers(u => u.map(x => x.id === userId ? { ...x, role } : x));
    } catch (err) {
      alert('שגיאה: ' + err.message);
    } finally {
      setActionBusy(null);
    }
  };

  const total    = users.length;
  const active   = users.filter(u => u.role === 'user').length;
  const admins   = users.filter(u => u.role === 'admin').length;
  const disabled = users.filter(u => u.role === 'disabled').length;

  return (
    <div dir="rtl" style={{ display: 'flex', flexDirection: 'column', height: '100%', background: UI.surface.page }}>

      {/* Sticky header */}
      <div style={{ position: 'sticky', top: 0, background: UI.surface.raised, borderBottom: `1px solid ${UI.line.hairline}`, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 }}>
        <span style={{ fontWeight: 800, fontSize: 16, color: UI.text.primary, display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="7" r="4" stroke={UI.text.secondary} strokeWidth="1.5"/>
            <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M16 3.13a4 4 0 010 7.75M21 21v-2a4 4 0 00-3-3.87" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          דאשבורד ניהול
        </span>
        <button onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: `1px solid ${UI.line.hairline}`, borderRadius: 3, padding: '5px 14px', fontSize: 13, fontWeight: 700, color: UI.text.secondary, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Icon name="x" size={13} color={UI.text.secondary}/> סגור
        </button>
      </div>

      <div style={{ padding: '16px 20px 60px', overflowY: 'auto', flex: 1 }}>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <StatCard label='סה"כ'   value={total}    color={UI.text.primary}    />
          <StatCard label="פעילים"  value={active}   color={UI.categorical[2]}   />
          <StatCard label="Admin"   value={admins}   color={UI.categorical[0]}   />
          <StatCard label="מושבתים" value={disabled} color={UI.categorical[3]}   />
        </div>

        {loading && (
          <div style={{ color: UI.text.secondary, textAlign: 'center', padding: 40, fontSize: 13 }}>טוען משתמשים...</div>
        )}

        {error && (
          <div style={{ color: UI.caution, textAlign: 'center', padding: 16, background: UI.cautionSoft, borderRadius: 6, fontSize: 13 }}>
            {error}
            <button onClick={loadUsers} style={{ display: 'block', margin: '10px auto 0', background: 'transparent', border: `1px solid ${UI.cautionLine}`, borderRadius: T.radius.md, padding: '5px 14px', color: UI.caution, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
              נסה שוב
            </button>
          </div>
        )}

        {!loading && !error && users.length === 0 && (
          <div style={{ color: UI.text.muted, textAlign: 'center', padding: 40, fontSize: 13 }}>אין משתמשים</div>
        )}

        {!loading && !error && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {users.map(user => {
              const isExpanded = expanded === user.id;
              const isSelf     = user.id === currentUserId;
              const busy       = actionBusy === user.id;
              const pfCount    = Array.isArray(user.portfolios) ? (user.portfolios[0]?.count ?? 0) : 0;

              return (
                <div key={user.id} style={{ background: UI.surface.raised, border: `1px solid ${isExpanded ? UI.line.strong : UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, overflow: 'hidden', transition: 'border-color .15s' }}>

                  {/* User row */}
                  <div
                    onClick={() => toggleExpand(user.id)}
                    style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
                  >
                    {/* Avatar */}
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: UI.fill.subtle, border: `1px solid ${UI.line.strong}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, color: UI.text.primary, flexShrink: 0 }}>
                      {(user.email?.[0] ?? '?').toUpperCase()}
                    </div>

                    {/* Email + meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: UI.text.primary, direction: 'ltr', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.email}
                      </div>
                      <div style={{ fontSize: 10, color: UI.text.muted, marginTop: 2 }}>
                        {dateNumeric(user.created_at)}
                        {' · '}
                        {pfCount} תיקים
                        {isSelf && <span style={{ color: UI.text.primary, marginRight: 6 }}>· אתה</span>}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <RoleBadge role={user.role} />

                      {!isSelf && user.role !== 'disabled' && (
                        <button
                          onClick={e => handleSetRole(e, user.id, 'disabled')}
                          disabled={busy}
                          style={{ background: UI.cautionSoft, border: `1px solid ${UI.cautionLine}`, borderRadius: 5, padding: '3px 9px', fontSize: 10, color: UI.caution, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? .5 : 1, fontFamily: 'inherit' }}
                        >
                          השבת
                        </button>
                      )}

                      {!isSelf && user.role === 'disabled' && (
                        <button
                          onClick={e => handleSetRole(e, user.id, 'user')}
                          disabled={busy}
                          style={{ background: UI.fill.subtle, border: `1px solid ${UI.line.strong}`, borderRadius: 5, padding: '3px 9px', fontSize: 10, color: UI.categorical[2], fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? .5 : 1, fontFamily: 'inherit' }}
                        >
                          הפעל
                        </button>
                      )}

                      {!isSelf && (
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteInfo(user); }}
                          style={{ background: 'transparent', border: `1px solid ${UI.line.hairline}`, borderRadius: 5, padding: '3px 8px', fontSize: 10, color: UI.text.muted, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          מחק
                        </button>
                      )}

                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }}>
                        <path d="M6 9l6 6 6-6" stroke={UI.text.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>

                  {/* Expanded: positions */}
                  {isExpanded && (
                    <div style={{ borderTop: `1px solid ${UI.line.hairline}`, padding: '12px 14px', background: UI.surface.page }}>
                      {posLoading === user.id && (
                        <div style={{ color: UI.text.secondary, fontSize: 12 }}>טוען החזקות...</div>
                      )}
                      {posLoading !== user.id && (!positions[user.id] || positions[user.id].length === 0) && (
                        <div style={{ color: UI.text.muted, fontSize: 12 }}>אין נתונים</div>
                      )}
                      {positions[user.id]?.map(pf => (
                        <div key={pf.id} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: UI.text.primary, fontWeight: 700, marginBottom: 6, letterSpacing: '.4px' }}>
                            {pf.name}
                          </div>
                          {(!pf.positions || pf.positions.length === 0) && (
                            <div style={{ fontSize: 11, color: UI.text.muted }}>תיק ריק</div>
                          )}
                          {pf.positions?.map(pos => (
                            <div key={pos.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${UI.line.hairline}` }}>
                              <span style={{ fontSize: 13, fontWeight: 800, color: UI.text.primary, minWidth: 60 }}>{pos.symbol}</span>
                              <span style={{ fontSize: 11, color: UI.text.secondary }}>{pos.quantity} יח' × ${pos.buy_price}</span>
                              {pos.buy_date
                                ? <span style={{ fontSize: 10, color: UI.text.muted }}>{dateNumeric(pos.buy_date)}</span>
                                : <span style={{ fontSize: 10, color: UI.text.muted, opacity: .4 }}>—</span>
                              }
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete instruction modal */}
      {deleteInfo && (
        <div
          style={{ position: 'fixed', inset: 0, background: UI.scrim, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setDeleteInfo(null)}
        >
          <div
            dir="rtl"
            style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: 24, maxWidth: 440, width: '100%' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, fontSize: 15, color: UI.text.primary, marginBottom: 4 }}>מחיקת משתמש</div>
            <div style={{ fontSize: 12, color: UI.text.secondary, marginBottom: 14, direction: 'ltr', wordBreak: 'break-all' }}>{deleteInfo.email}</div>

            <div style={{ fontSize: 12, color: UI.text.secondary, marginBottom: 14, lineHeight: 1.7 }}>
              מחיקה מ-<code style={{ background: UI.surface.page, padding: '1px 5px', borderRadius: 3, color: UI.text.primary, fontSize: 11 }}>auth.users</code> דורשת Service Role Key ואינה אפשרית מהאפליקציה מטעמי אבטחה.
              הרץ את הפקודה הבאה ב-Supabase SQL Editor:
            </div>

            <div style={{ background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: UI.text.muted, marginBottom: 6, fontWeight: 700 }}>Supabase SQL Editor</div>
              <code style={{ fontSize: 11, color: UI.text.secondary, display: 'block', direction: 'ltr', lineHeight: 1.8, wordBreak: 'break-all' }}>
                DELETE FROM auth.users<br/>
                WHERE id = '{deleteInfo.id}';
              </code>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: UI.caution, background: UI.cautionSoft, border: `1px solid ${UI.cautionLine}`, borderRadius: 6, padding: '8px 12px', marginBottom: 16 }}>
              <Icon name="warning" size={14} color={UI.caution} style={{ marginTop: 1, flexShrink: 0 }}/>
              <span>פעולה זו בלתי הפיכה. כל הנתונים הקשורים למשתמש יימחקו לצמיתות.</span>
            </div>

            <button
              onClick={() => setDeleteInfo(null)}
              style={{ background: UI.line.strong, border: 'none', borderRadius: 6, padding: '9px 20px', fontSize: 13, fontWeight: 700, color: UI.text.primary, cursor: 'pointer', width: '100%', fontFamily: 'inherit' }}
            >
              הבנתי
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
